import React, { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useHTMLData, useI18N, useTaggingInformationData } from "common/hooks";
import { Helmet } from "react-helmet";
import Loader from "@/components/common/Loader";
import DOMPurify from "dompurify";
import { useCustomToast } from "@/hooks/useCustomToast";

export const Route = createFileRoute("/content/$slug")({
  component: Content,
});

function Content() {
  const { slug } = Route.useParams();
  const i18n = useI18N();

  const showToast = useCustomToast();

  const { setType, data, error, isLoading } = useHTMLData();
  const {
    content,
    error: isTaggingContentError,
    isLoading: isTaggingContentLoading,
  } = useTaggingInformationData();

  const [visibleBlocks, setVisibleBlocks] = useState({});

  useEffect(() => {
    if (slug) {
      setType(slug);
    }
  }, [setType, slug]);

  useEffect(() => {
    if (error) {    
      showToast(error?.message, "error");
    }
  }, [error, slug]);

  useEffect(() => {
    if (isTaggingContentError) {
      showToast(isTaggingContentError?.message, "error");
    }
  }, [isTaggingContentError, slug]);

  const toggleVisibility = (id) => {
    setVisibleBlocks((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const sanitizedHtml = data?.data?.body
    ? DOMPurify.sanitize(data?.data?.body)
    : "";

  return (
    <div>
      <Helmet>
        <title>{data?.data?.title} | Sociability</title>
      </Helmet>

      {(isLoading || isTaggingContentLoading) && <Loader />}

      <section className="px-4 py-6">
        <div className="max-w-[1160px] mx-auto pb-10">
          <h1 className="desktop-h2-small-bold text-primary-800 dark:text-primary-150 mb-3">
            {data?.data?.title}
          </h1>
          {slug === "tagging-information" && (
            <h2 className="paragraph-small-semibold leading-15 mb-5 ml-1 text-primary-800 dark:text-primary-150">
              {i18n.get("pleaseClickTaggingTitle")}
            </h2>
          )}

          {slug === "tagging-information" &&
            content?.length > 0 &&
            content.map((block, ind) => {
              const blockId = block.id ? block.id : `index_${ind}`;
              return (
                <div key={blockId} className="mb-10" id={blockId}>
                  <h3
                    role="none"
                    className="desktop-h4-medium-semi-bold text-primary-800 dark:text-primary-150 mb-3"
                  >
                    {block.title}
                  </h3>

                  <div className="flex items-start gap-12 p-6 rounded-xl border border-violet-200">
                    <div className="flex-grow flex flex-col gap-6">
                      <h4
                        className="desktop-h3-small-semi-bold  text-primary-800 dark:text-primary-150 cursor-pointer"
                        onClick={() => toggleVisibility(blockId)}
                      >
                        {block?.children[0]?.title}
                      </h4>
                      {visibleBlocks[blockId] && (
                        <div id={`block_${blockId}`}>
                          <div
                            className="list-container paragraph-medium-regular text-primary-800 dark:text-primary-150"
                            dangerouslySetInnerHTML={{
                              __html: block?.children[0]?.content
                                ? DOMPurify.sanitize(
                                    block?.children[0]?.content
                                  )
                                : "",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

          {slug !== "tagging-information" && (
            <div
              id="contentArea"
              className="paragraph-medium-regular text-primary-800 dark:text-primary-150"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          )}
        </div>
      </section>
    </div>
  );
}

export default Content;
